import { Component, computed, inject, effect, signal, OnInit } from '@angular/core';
import { DateTime } from 'luxon';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatStepperModule } from '@angular/material/stepper';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { Router } from '@angular/router';
import { ContactStore, BankStore } from '../../core/stores/base.store';
import { Loan, BankTransaction, InitBankTransaction, InitLoan } from '../../core/models';
import { AmortizationRow } from '../../core/services';
import { NotificationUtils, LoanUtils } from '../../core/utils';
import { FuseConfirmationService } from '../../core/services/confirmation/confirmation.service';
import { formatDateAsYYYYMMDD, parseYYYYMMDDToDate } from '../../core/utils/date.utils';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { NumberFormatDirective } from '../../core/utils/comma.utils';

@Component({
  selector: 'app-loan',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatStepperModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatIconModule,
    MatToolbarModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatDatepickerModule,
    MatTableModule,
    MatCardModule,
    MatButtonToggleModule,
    MatNativeDateModule,
    MatCheckboxModule,
    NumberFormatDirective
  ],
  templateUrl: './loan.component.html',
  styleUrl: './loan.component.css'
})
export class LoanComponent implements OnInit {
  // Make LoanUtils accessible in the template
  LoanUtils = LoanUtils;
  amortizationSchedule: AmortizationRow[] = [];
  vrr: number = 0;
  totalInterest: number = 0;
  totalPayments: number = 0;
  basicDetailsForm: FormGroup;
  paymentTermsForm: FormGroup;
  isLinear = true; // Force user to complete steps in sequence
  private contactStore = inject(ContactStore);
  private bankStore = inject(BankStore);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  contacts = computed(() => this.contactStore.entities());
  filteredContacts = computed(() => {
    // Filter contacts for Self or Borrower
    const filtered = this.contacts().filter(contact => 
      contact.category === 'Self' || contact.category === 'Borrower'
    );
    
    // Sort contacts: Self first, then alphabetically by first_name
    return filtered.sort((a, b) => {
      // Put Self contacts first
      if (a.category === 'Self' && b.category !== 'Self') {
        return -1;
      }
      if (a.category !== 'Self' && b.category === 'Self') {
        return 1;
      }
      
      // Then sort alphabetically by first_name
      const aName = a.first_name || '';
      const bName = b.first_name || '';
      return aName.localeCompare(bName);
    });
  });
  loading = computed(() => this.contactStore.loading());
  isEdit = false;
  #fuseConfirmationService = inject(FuseConfirmationService);
  newLoanCreated = signal(false); // Flag to track if a new loan was created
  currentStepIndex = 0;

  constructor() {
    // Create basic details form
    this.basicDetailsForm = this.fb.group({
      loanType: ['new'], // Add loan type field with default value 'new'
      contact: ['', Validators.required],
      loanName: ['', Validators.required],
      state: ['active', Validators.required],
      closeDate: ["", Validators.required],
      startPaymentDate: ["", Validators.required],
      nextPaymentDate: ["", Validators.required],
      midLifeDate: [''], // Changed from anchorDate to midLifeDate
      lateFee: ['50', Validators.required],
      gracePeriod: ['15', Validators.required],
      fundFromBank: [true] // Default to true
    });

    // Create payment terms form
    this.paymentTermsForm = this.fb.group({
      principal: [null, [Validators.required, Validators.min(0)]],
      monthlyPayment: [null, [Validators.min(0)]],
      interestRate: [null, [Validators.min(0), Validators.max(100)]],
      term: [null, [Validators.required, Validators.min(1)]], // Term in frequency units
      frequency: ['monthly', [Validators.required]],
      midLifeBalance: [null, [Validators.min(0)]] // Balance for mid-life loans
    });

    // Create an effect to watch for selectedLoan changes and create transactions when needed
    effect(() => {
      const selectedLoan = this.bankStore.selectedLoan();
      
      // Only proceed if we have a selected loan and the newLoanCreated flag is true
      if (selectedLoan && this.newLoanCreated()) {
        // Reset the flag immediately to prevent multiple executions
        this.newLoanCreated.set(false);
        
        // Create the initial contact transaction
        this.createInitialContactTransaction(selectedLoan);
        // Navigate to the banks page
        this.router.navigate(['/banks']);
      }
    });
  }

  ngOnInit() {
    // Check if we're editing an existing loan
    if (this.bankStore.selectedLoan()) {
      this.loadLoan();
    } else {
      this.setDefaultValues();
    }

    // Listen for changes to the loan type
    this.basicDetailsForm.get('loanType')?.valueChanges.subscribe(value => {
      if (value === 'new') {
        // For new loans, set start payment date equal to next payment date
        const nextPaymentDate = this.basicDetailsForm.get('nextPaymentDate')?.value;
        this.basicDetailsForm.get('startPaymentDate')?.setValue(nextPaymentDate);
        // For new loans, enable fund from bank only if state is active
        if (this.basicDetailsForm.get('state')?.value === 'active') {
          this.basicDetailsForm.get('fundFromBank')?.setValue(true);
        }
      } else if (value === 'mid-life') {
        // For mid-life loans, disable fund from bank
        this.basicDetailsForm.get('fundFromBank')?.setValue(false);
      }
    });
    
    // Listen for changes to the state field
    this.basicDetailsForm.get('state')?.valueChanges.subscribe(value => {
      // If state is not active, uncheck fund from bank
      if (value !== 'active') {
        this.basicDetailsForm.get('fundFromBank')?.setValue(false);
      }
    });
    
    // Also listen for changes to next payment date when loan type is 'new'
    this.basicDetailsForm.get('nextPaymentDate')?.valueChanges.subscribe(value => {
      if (this.basicDetailsForm.get('loanType')?.value === 'new') {
        this.basicDetailsForm.get('startPaymentDate')?.setValue(value);
      }
    });
    
    // Listen for frequency changes to recalculate payment
    this.paymentTermsForm.get('frequency')?.valueChanges.subscribe(() => {
      if (this.canCalculatePayment()) {
        this.calculatePayment();
      }
    });

    // Set default frequency to monthly
    this.paymentTermsForm.patchValue({ frequency: 'monthly' });
  }

  // Track which fields can be calculated
  canCalculatePayment() {
    const principal = LoanUtils.parseFormattedNumber(this.paymentTermsForm?.get('principal')?.value);
    const interestRate = Number(this.paymentTermsForm?.get('interestRate')?.value);
    const term = Number(this.paymentTermsForm?.get('term')?.value);
    return principal > 0 && interestRate > 0 && term > 0;
  }

  getTermLabel(): string {
    const frequency = this.paymentTermsForm.get('frequency')?.value;
    switch (frequency) {
      case 'annually': return 'Years';
      case 'semi-annually': return 'Half Years';
      case 'quarterly': return 'Quarters';
      case 'monthly': return 'Months';
      case 'bi-weekly': return 'Bi-weeks';
      case 'weekly': return 'Weeks';
      default: return 'Periods';
    }
  }

  getMaxTermForFrequency(): number {
    const frequency = this.paymentTermsForm.get('frequency')?.value;
    const maxMonths = 600; // 50 years
    return this.convertMonthsToTerm(maxMonths, frequency);
  }

  private convertTermToMonths(term: number, frequency: string): number {
    return LoanUtils.convertTermToMonths(term, frequency);
  }

  private convertMonthsToTerm(months: number, frequency: string): number {
    switch (frequency) {
      case 'annually': return Math.round(months / 12);
      case 'semi-annually': return Math.round(months / 6);
      case 'quarterly': return Math.round(months / 3);
      case 'monthly': return months;
      case 'bi-weekly': return Math.round(months * (26 / 12));
      case 'weekly': return Math.round(months * (52 / 12));
      default: return months;
    }
  }

  canCalculatePrincipal() {
    const monthlyPayment = LoanUtils.parseFormattedNumber(this.paymentTermsForm?.get('monthlyPayment')?.value);
    const interestRate = Number(this.paymentTermsForm?.get('interestRate')?.value);
    const term = Number(this.paymentTermsForm?.get('term')?.value);
    return monthlyPayment > 0 && interestRate > 0 && term > 0;
  }

  canCalculateInterest() {
    const principal = LoanUtils.parseFormattedNumber(this.paymentTermsForm?.get('principal')?.value);
    const monthlyPayment = LoanUtils.parseFormattedNumber(this.paymentTermsForm?.get('monthlyPayment')?.value);
    const term = Number(this.paymentTermsForm?.get('term')?.value);
    return principal > 0 && monthlyPayment > 0 && term > 0;
  }

  canCalculateTerm() {
    const principal = LoanUtils.parseFormattedNumber(this.paymentTermsForm?.get('principal')?.value);
    const monthlyPayment = LoanUtils.parseFormattedNumber(this.paymentTermsForm?.get('monthlyPayment')?.value);
    const interestRate = Number(this.paymentTermsForm?.get('interestRate')?.value);
    return principal > 0 && monthlyPayment > 0 && interestRate > 0;
  }


  delete() {
    const confirmation = this.#fuseConfirmationService.open({
      title: 'Delete Loan',
      message: 'Are you sure you want to delete this loan? This action cannot be undone!',
      actions: {
        confirm: {
          label: 'Delete'
        }
      }
    })

    confirmation.afterClosed().subscribe((result) => {
      if (result === 'confirmed') {
        this.bankStore.deleteLoan({loan:this.bankStore.selectedLoan(), endpoint: 'loan', collectionName: 'loans_collection'});
      }
    })
    this.router.navigate(['/banks']);
  }

  get_name(contactId: number): string {
    if (!this.contacts() || !contactId) return 'Unknown';
    
    const contact = this.contacts().find(c => c.id === contactId);
    if (!contact) {
      console.warn(`Contact with ID ${contactId} not found`);
      return 'Unknown';
    }
    
    return `${contact.first_name} ${contact.last_name}`;
  }

  onCancel() {
    this.router.navigate(['/banks']);
  }

  calculateReviewData() {
    const { term, frequency, interestRate } = this.paymentTermsForm.value;
    const { nextPaymentDate, closeDate, startPaymentDate } = this.basicDetailsForm.value;

    // Parse formatted numbers
    const principal = this.parseFormattedNumber(this.paymentTermsForm.get('principal')?.value);
    const monthlyPayment = this.parseFormattedNumber(this.paymentTermsForm.get('monthlyPayment')?.value);
    const midLifeBalance = this.parseFormattedNumber(this.paymentTermsForm.get('midLifeBalance')?.value);

    if (nextPaymentDate instanceof Date && startPaymentDate instanceof Date) {
      // Continue with date processing
    } else {
      return; // Exit if not valid dates
    }

    if (principal && monthlyPayment && term && nextPaymentDate && closeDate && startPaymentDate) {
      const termMonths = this.convertTermToMonths(term, frequency);
      const payment = monthlyPayment;
      
      // Get the date to use for the schedule - use Luxon for consistent handling
      const nextPaymentDateLuxon = DateTime.fromJSDate(nextPaymentDate).setZone('UTC').set({ hour: 12, minute: 0, second: 0, millisecond: 0 });
      const startPaymentDateLuxon = DateTime.fromJSDate(startPaymentDate).setZone('UTC').set({ hour: 12, minute: 0, second: 0, millisecond: 0 });
      
      // Always set midLifeDate to the nextPaymentDate
      this.basicDetailsForm.get('midLifeDate').setValue(nextPaymentDate);
      const midLifeDateLuxon = nextPaymentDateLuxon;
      
      // Convert to JS Date for the schedule generation
      const dateForSchedule = startPaymentDateLuxon.toJSDate();
      const closeDateObj = closeDate instanceof Date ? closeDate : new Date(closeDate);
      
      // Generate initial amortization schedule using LoanUtils with the start payment date and close date
      let amortizationSchedule = LoanUtils.generatePaymentSchedule(
        Number(principal),
        Number(payment),
        Number(interestRate),
        Number(termMonths),
        dateForSchedule,  // Use start payment date for the schedule
        frequency,
        closeDateObj  // Pass the close date for the funding row
      );

      // Mark payments as "paid" if they fall between start_payment_date and next_payment_date
      let nextPaymentIndex = -1;
      let midLifePaymentIndex = -1;
      
      // Find the mid life payment index
      amortizationSchedule.forEach((row, index) => {
        const paymentDateLuxon = DateTime.fromJSDate(row.paymentDate).setZone('UTC');
        
        // Find the payment that corresponds to the mid life date
        if (midLifePaymentIndex === -1) {
          // Find the first payment on or after the mid life date
          if (paymentDateLuxon >= midLifeDateLuxon) {
            midLifePaymentIndex = index;
          }
        }
        
        // Mark payments as paid if they're before the next payment date
        if (paymentDateLuxon < nextPaymentDateLuxon) {
          row.paid = true;
        } else if (nextPaymentIndex === -1 && paymentDateLuxon >= nextPaymentDateLuxon) {
          // This is the next payment
          nextPaymentIndex = index;
        }
      });
      
      // If we couldn't find a mid life payment, use the next payment
      if (midLifePaymentIndex === -1) {
        midLifePaymentIndex = nextPaymentIndex;
      }

      // Apply mid life balance adjustment if provided at the mid life date
      if (midLifeBalance && !isNaN(midLifeBalance) && midLifePaymentIndex !== -1) {
        const adjustmentAmount = Number(midLifeBalance);
        
        if (adjustmentAmount !== 0) {
          // Get the remaining balance at the mid life payment date
          let remainingBalance = midLifePaymentIndex > 0 
            ? amortizationSchedule[midLifePaymentIndex - 1].remainingBalance 
            : Number(principal);
          
          // Set the remaining balance to the mid-life balance directly
          remainingBalance = adjustmentAmount;
          
          // If we have a valid remaining balance, recalculate the amortization from this point
          if (remainingBalance > 0) {
            // Calculate remaining term
            const remainingTermMonths = termMonths - midLifePaymentIndex;
            
            // Get the date of the mid life payment
            const midLifePaymentDateJS = midLifePaymentIndex < amortizationSchedule.length 
              ? amortizationSchedule[midLifePaymentIndex].paymentDate 
              : midLifeDateLuxon.toJSDate();
            
            // Generate a new schedule for the remaining term
            const remainingSchedule = LoanUtils.generatePaymentSchedule(
              remainingBalance,
              Number(payment),
              Number(interestRate),
              remainingTermMonths,
              midLifePaymentDateJS,
              frequency
            );
            
            // Replace the remaining portion of the original schedule
            if (midLifePaymentIndex > 0) {
              // Keep the paid payments and replace the rest
              amortizationSchedule = [
                ...amortizationSchedule.slice(0, midLifePaymentIndex),
                ...remainingSchedule
              ];
            } else {
              // Replace the entire schedule
              amortizationSchedule = remainingSchedule;
            }
            
            // Re-mark payments as paid if they're before the next payment date
            if (nextPaymentDateLuxon > startPaymentDateLuxon) {
              amortizationSchedule.forEach(row => {
                const paymentDateLuxon = DateTime.fromJSDate(row.paymentDate).setZone('UTC');
                if (paymentDateLuxon < nextPaymentDateLuxon) {
                  row.paid = true;
                }
              });
            }
          }
        }
      }
      
      // Set the final amortization schedule
      this.amortizationSchedule = amortizationSchedule;
            
      // For mid-life loans, set the midLifeBalance form control to the balance at the next payment date
      const isMidLife = this.basicDetailsForm.get('loanType')?.value === 'mid-life';
      if (amortizationSchedule.length > 0) {
        // Find the payment that corresponds to the next payment date
        const nextPaymentDateValue = this.basicDetailsForm.get('nextPaymentDate')?.value;
        if (nextPaymentDateValue instanceof Date) {
          const nextPaymentDateLuxon = DateTime.fromJSDate(nextPaymentDateValue).setZone('UTC').set({ hour: 12, minute: 0, second: 0, millisecond: 0 });
          
          // Find the payment that is closest to but not before the next payment date
          let balanceAtNextPayment = 0;
          
          // If we have a next payment index, use the balance from the previous payment
          if (nextPaymentIndex > 0) {
            // Use the balance from the payment before the next payment
            balanceAtNextPayment = amortizationSchedule[nextPaymentIndex - 1].remainingBalance;
          } else if (nextPaymentIndex === 0) {
            // If the next payment is the first payment, use the principal amount
            balanceAtNextPayment = Number(principal);
          } else {
            // If we couldn't find a next payment, use the first payment's balance
            balanceAtNextPayment = amortizationSchedule[0].remainingBalance;
          }
          
          // Set the mid-life balance form control if it's a mid-life loan
          if (isMidLife) {
            this.paymentTermsForm.get('midLifeBalance')?.setValue(balanceAtNextPayment.toFixed(2));
          }
        }
      }

      // Calculate totals
      this.calculateAmortizationTotals();
    }
  }

  /**
   * Returns the amortization schedule including the funding row and future payments
   * This is used to display the funding row and future payments in the amortization table
   */
  getAmortizationScheduleFromNextPayment(): AmortizationRow[] {
    if (!this.amortizationSchedule || this.amortizationSchedule.length === 0) {
      return [];
    }
    
    // Always include the funding row (row 0)
    const fundingRow = this.amortizationSchedule.find(row => row.paymentNumber === 0);
    
    // Find the first non-paid payment (the next payment)
    const nextPaymentIndex = this.amortizationSchedule.findIndex(row => !row.paid && row.paymentNumber > 0);
    
    // If all payments are paid or no next payment found, return the full schedule
    if (nextPaymentIndex === -1) {
      return this.amortizationSchedule;
    }
    
    // Return the funding row and the payments from the next payment onward
    const futurePayments = this.amortizationSchedule.slice(nextPaymentIndex);
    return fundingRow ? [fundingRow, ...futurePayments] : futurePayments;
  }

  async onSubmit() {
    if (this.basicDetailsForm.valid && this.paymentTermsForm.valid) {
      const basicDetails = this.basicDetailsForm.value;
      const paymentTerms = this.paymentTermsForm.value;
      
      // Parse formatted numbers
      const principal = LoanUtils.parseFormattedNumber(paymentTerms.principal);
      const monthlyPayment = LoanUtils.parseFormattedNumber(paymentTerms.monthlyPayment);
      let midLifeBalance = null;
      
      // Ensure mid-life balance is set for mid-life loans
      if (basicDetails.loanType === 'mid-life' && (!midLifeBalance || isNaN(midLifeBalance))) {
        // If no mid-life balance is provided, calculate it based on the amortization schedule
        if (this.amortizationSchedule && this.amortizationSchedule.length > 0) {
          // Use the balance from the first payment in the schedule
          midLifeBalance = this.amortizationSchedule[0].remainingBalance;
        } 
      }
      
      // Format dates as YYYY-MM-DD to avoid timezone issues
      const formattedNextPaymentDate = formatDateAsYYYYMMDD(basicDetails.nextPaymentDate);
      const formattedCloseDate = formatDateAsYYYYMMDD(basicDetails.closeDate);
      const formattedStartDate = formatDateAsYYYYMMDD(basicDetails.startPaymentDate);
      
      // For mid-life loans, set the mid life date
      // For new loans, set the mid life date to null
      let formattedMidLifeDate = null;
      if (basicDetails.loanType === 'mid-life') {
        if (basicDetails.midLifeDate) {
          formattedMidLifeDate = formatDateAsYYYYMMDD(basicDetails.midLifeDate);
        } else if (!this.isEdit) {
          // Default to next payment date for new mid-life loans with no specified date
          formattedMidLifeDate = formattedNextPaymentDate;
          this.basicDetailsForm.patchValue({ midLifeDate: basicDetails.nextPaymentDate });
        }
      }

      let remainingBalance = LoanUtils.parseFormattedNumber(paymentTerms.principal);
      // For mid-life or existing loans, use the mid-life balance as the remaining balance
      if (this.basicDetailsForm.get('loanType')?.value === 'mid-life' || this.basicDetailsForm.get('loanType')?.value === 'existing') {
        remainingBalance = LoanUtils.parseFormattedNumber(paymentTerms.midLifeBalance);
      }

      // Calculate the next payment number for the loan
      let nextPaymentNumber = 1;
      if (this.basicDetailsForm.get('loanType')?.value === 'mid-life') {
        // For mid-life loans, calculate the payment number based on the mid-life date and start date
        const startDate = new Date(formattedStartDate);
        const midLifeDate = new Date(formattedMidLifeDate || formattedNextPaymentDate);
        const frequency = paymentTerms.frequency;
        
        // Calculate the number of periods between start date and mid-life date
        const startDateTime = DateTime.fromJSDate(startDate);
        const midLifeDateTime = DateTime.fromJSDate(midLifeDate);
        
        // Calculate difference based on frequency
        let periodsBetween = 0;
        
        switch (frequency) {
          case 'annually':
            periodsBetween = midLifeDateTime.diff(startDateTime, 'years').years;
            break;
          case 'semi-annually':
            periodsBetween = midLifeDateTime.diff(startDateTime, 'months').months / 6;
            break;
          case 'quarterly':
            periodsBetween = midLifeDateTime.diff(startDateTime, 'months').months / 3;
            break;
          case 'monthly':
            periodsBetween = midLifeDateTime.diff(startDateTime, 'months').months;
            break;
          case 'bi-weekly':
            periodsBetween = midLifeDateTime.diff(startDateTime, 'weeks').weeks / 2;
            break;
          case 'weekly':
            periodsBetween = midLifeDateTime.diff(startDateTime, 'weeks').weeks;
            break;
          default:
            periodsBetween = midLifeDateTime.diff(startDateTime, 'months').months;
        }
        
        // Round to the nearest whole number and add 1 (since payment numbers start at 1)
        nextPaymentNumber = Math.max(1, Math.round(periodsBetween) + 1);
      }
      
      // Use formatted dates to avoid timezone issues
      
      // Ensure we have a valid bank_id
      const selectedBank = this.bankStore.selected();
      if (!selectedBank || !selectedBank.id) {
        NotificationUtils.getInstance().showError('No bank selected. Please select a bank first.');
        return;
      }

      // Calculate next principal and interest using LoanUtils.getAmortizationPayment
      // Create a temporary loan object to pass to the getAmortizationPayment method
      const tempLoan: Loan = {
        ...InitLoan,
        amount: principal,
        payment: monthlyPayment,
        interest_rate: Number(paymentTerms.interestRate),
        term: this.convertTermToMonths(paymentTerms.term, paymentTerms.frequency),
        frequency: paymentTerms.frequency,
        remaining_balance: remainingBalance
      };
      
      // Get the next payment details
      const nextPaymentDetails = LoanUtils.getAmortizationPayment(tempLoan, nextPaymentNumber, true);
      const nextPrincipal = nextPaymentDetails.principal;
      const nextInterest = nextPaymentDetails.interest;
      
      const loan: Loan = {
        ...InitLoan,
        id: this.isEdit ? this.bankStore.selectedLoan()?.id : 0,
        bank_id: selectedBank.id,
        contact_id: basicDetails.contact,
        loan_type: basicDetails.loanType, // Include the loan_type field
        name: basicDetails.loanName,
        amount: principal,
        payment: monthlyPayment,
        interest_rate: Number(paymentTerms.interestRate),
        term: this.convertTermToMonths(paymentTerms.term, paymentTerms.frequency),
        frequency: paymentTerms.frequency,
        state: basicDetails.state,
        close_date: formattedCloseDate,
        start_payment_date: formattedStartDate,
        next_payment_date: formattedNextPaymentDate,
        next_payment_number: nextPaymentNumber,
        next_principal: nextPrincipal,
        next_interest: nextInterest,
        mid_life_date: formattedMidLifeDate,
        mid_life_balance: midLifeBalance,
        remaining_balance: remainingBalance,
        late_fee: Number(basicDetails.lateFee),
        grace_period: Number(basicDetails.gracePeriod)
      };

      try {
        // Check if we're changing a loan from pending to active and need to fund from bank
        const isPendingToActive = this.isEdit && 
                                 this.bankStore.selectedLoan()?.state === 'pending' && 
                                 loan.state === 'active' && 
                                 basicDetails.fundFromBank;
        
        if (this.isEdit) {
          // Update the loan
          this.bankStore.updateLoan({
            loan,
            endpoint: 'loan',
            collectionName: 'loans_collection'
          });
          
          // If changing from pending to active and funding from bank, create a transaction
          if (isPendingToActive) {
            this.createInitialContactTransaction(loan);
          }
          
          this.router.navigate(['/banks']);
          // NotificationUtils.getInstance().showActionResult(true, 'loan', 'updated');
        } else {
          delete loan.id
          this.bankStore.createLoan({
            loan,
            endpoint: 'loan',
            collectionName: 'loans_collection'
          });
          if (loan.state === 'active' && !this.isEdit) {
            this.newLoanCreated.set(true);
          }
          else {
            this.router.navigate(['/banks']);
          }
        }
      } catch (error) {
        console.error('Error saving loan:', error);
        NotificationUtils.getInstance().showActionResult(false, 'loan', this.isEdit ? 'updated' : 'created');
      }
    } else {
      // Mark all form controls as touched to trigger validation messages
      this.markFormGroupTouched(this.basicDetailsForm);
      this.markFormGroupTouched(this.paymentTermsForm);
      
      NotificationUtils.getInstance().showError('Please fix the errors in the form before submitting');
    }
  }

  /**
   * Recalculates the amortization schedule after a mid life balance adjustment is made
   */
  recalculateAmortization() {
    const midLifeBalance = LoanUtils.parseFormattedNumber(this.paymentTermsForm.get('midLifeBalance')?.value);
    
    if (midLifeBalance !== null && !isNaN(midLifeBalance)) {
      // Calculate the review data which will apply the adjustment
      this.calculateReviewData();
      
      // Show success notification
      NotificationUtils.getInstance().showSuccess('Amortization schedule recalculated with mid term balance adjustment');
    } else {
      NotificationUtils.getInstance().showSuccess('No mid term balance adjustment entered');
    }
  }

  /**
   * Helper function to convert string values with commas to numbers
   * Uses the LoanUtils implementation for consistency
   */
  parseFormattedNumber(value: string | number | null): number | null {
    return LoanUtils.parseFormattedNumber(value);
  }

  /**
   * Recalculates the amortization table based on the mid-life balance
   * This takes the value of the mid-life-balance and recalculates the remainder of the table,
   * using the new balance as the starting point
   */
  recalculateAmortizationFromMidLife() {
    // Get the mid-life balance from the form
    const midLifeBalance = this.parseFormattedNumber(this.paymentTermsForm.get('midLifeBalance')?.value);
    
    // Validate the mid-life balance
    if (!midLifeBalance || isNaN(midLifeBalance) || midLifeBalance <= 0) {
      const notificationUtils = NotificationUtils.getInstance();
      notificationUtils.showError('Please enter a valid mid-life balance greater than zero.');
      return;
    }
    
    // Get the necessary values from the form
    const { term, frequency, interestRate } = this.paymentTermsForm.value;
    const { nextPaymentDate } = this.basicDetailsForm.value;
    const monthlyPayment = this.parseFormattedNumber(this.paymentTermsForm.get('monthlyPayment')?.value);
    
    if (!nextPaymentDate || !(nextPaymentDate instanceof Date)) {
      const notificationUtils = NotificationUtils.getInstance();
      notificationUtils.showError('Please enter a valid next payment date.');
      return;
    }
    
    // Calculate term in months
    const termMonths = this.convertTermToMonths(term, frequency);
    
    // Create a date object for the next payment date
    const nextPaymentDateLuxon = DateTime.fromJSDate(nextPaymentDate).setZone('UTC').set({ hour: 12, minute: 0, second: 0, millisecond: 0 });
    
    // Store the original amortization schedule to preserve payment numbers
    const originalSchedule = this.amortizationSchedule || [];
    
    // Find the payment number to start with
    let startPaymentNumber = 1;
    
    // If we have an existing schedule, find the payment number for the next payment date
    if (originalSchedule.length > 0) {
      // Find the payment that corresponds to the next payment date
      for (let i = 0; i < originalSchedule.length; i++) {
        const paymentDateLuxon = DateTime.fromJSDate(originalSchedule[i].paymentDate).setZone('UTC');
        if (paymentDateLuxon >= nextPaymentDateLuxon) {
          startPaymentNumber = originalSchedule[i].paymentNumber;
          break;
        }
      }
    }
    
    // Generate a new amortization schedule using the mid-life balance as the starting balance
    const newSchedule = LoanUtils.generatePaymentSchedule(
      Number(midLifeBalance),
      Number(monthlyPayment),
      Number(interestRate),
      Number(termMonths),
      nextPaymentDateLuxon.toJSDate(),
      frequency
    );
    
    // Update the payment numbers in the new schedule to preserve the original numbering
    newSchedule.forEach((payment, index) => {
      payment.paymentNumber = startPaymentNumber + index;
    });
    
    // Update the amortization schedule
    this.amortizationSchedule = newSchedule;
    
    // Calculate totals based on the new schedule
    this.calculateAmortizationTotals();
    
    // Show success message
    const notificationUtils = NotificationUtils.getInstance();
    notificationUtils.showSuccess('Amortization schedule recalculated successfully.');
  }
  
  /**
   * Calculates the totals for the amortization schedule
   * Updates the totalPayments, totalInterest, and vrr properties
   */
  calculateAmortizationTotals() {
    if (!this.amortizationSchedule || this.amortizationSchedule.length === 0) {
      return;
    }
    
    // Calculate total payments and total interest
    let totalPayments = 0;
    let totalInterest = 0;
    
    this.amortizationSchedule.forEach(row => {
      totalPayments += row.payment;
      totalInterest += row.interest;
    });
    
    // Update the component properties
    this.totalPayments = totalPayments;
    this.totalInterest = totalInterest;
    
    // Calculate Volume Rate of Return (VRR)
    const principal = this.parseFormattedNumber(this.paymentTermsForm.get('principal')?.value) || 0;
    const payment = this.parseFormattedNumber(this.paymentTermsForm.get('monthlyPayment')?.value) || 0;
    const termMonths = this.convertTermToMonths(
      Number(this.paymentTermsForm.get('term')?.value), 
      this.paymentTermsForm.get('frequency')?.value
    );
    const closeDate = this.basicDetailsForm.get('closeDate')?.value;
    const startDate = this.basicDetailsForm.get('startPaymentDate')?.value;

    if (principal > 0 && payment > 0 && termMonths > 0 && closeDate && startDate) {
      try {
        this.vrr = LoanUtils.calculateVRR(
          principal,
          payment,
          termMonths,
          startDate,
          closeDate,
          this.paymentTermsForm.get('frequency')?.value
        );
      } catch (error) {
        console.error('Error calculating VRR:', error);
        // Fallback to simple calculation if there's an error
        this.vrr = principal > 0 ? (totalInterest / principal) * 100 : 0;
      }
    } else {
      this.vrr = principal > 0 ? (totalInterest / principal) * 100 : 0;
    }
  }

  calculatePayment() {
    const principal = LoanUtils.parseFormattedNumber(this.paymentTermsForm.get('principal')?.value);
    const interestRate = Number(this.paymentTermsForm.get('interestRate')?.value);
    const term = Number(this.paymentTermsForm.get('term')?.value);
    const frequency = this.paymentTermsForm.get('frequency')?.value;
    
    if (principal && interestRate && term && frequency) {
      const termMonths = this.convertTermToMonths(term, frequency);
      const monthlyRate = interestRate / 100 / 12;
      
      // Use the formula: PMT = P * r * (1 + r)^n / ((1 + r)^n - 1)
      const payment = principal * monthlyRate * Math.pow(1 + monthlyRate, termMonths) / (Math.pow(1 + monthlyRate, termMonths) - 1);
      
      this.paymentTermsForm.get('monthlyPayment')?.setValue(payment.toFixed(2));
      
      // Calculate current balance
      this.calculateCurrentBalance();
    }
  }

  calculatePrincipal() {
    const payment = LoanUtils.parseFormattedNumber(this.paymentTermsForm.get('monthlyPayment')?.value);
    const interestRate = Number(this.paymentTermsForm.get('interestRate')?.value);
    const term = Number(this.paymentTermsForm.get('term')?.value);
    const frequency = this.paymentTermsForm.get('frequency')?.value;
    
    if (payment && interestRate && term && frequency) {
      const termMonths = this.convertTermToMonths(term, frequency);
      const monthlyRate = interestRate / 100 / 12;
      
      // Use the formula: P = PMT * ((1 + r)^n - 1) / (r * (1 + r)^n)
      const principal = payment * (Math.pow(1 + monthlyRate, termMonths) - 1) / (monthlyRate * Math.pow(1 + monthlyRate, termMonths));
      
      this.paymentTermsForm.get('principal')?.setValue(principal.toFixed(2));
      
      // Calculate current balance
      this.calculateCurrentBalance();
    }
  }

  calculateInterest() {
    if (this.canCalculateInterest()) {
      const principal = LoanUtils.parseFormattedNumber(this.paymentTermsForm.get('principal')?.value);
      const monthlyPayment = LoanUtils.parseFormattedNumber(this.paymentTermsForm.get('monthlyPayment')?.value);
      const term = Number(this.paymentTermsForm.get('term')?.value);
      const frequencyValue = this.paymentTermsForm.get('frequency')?.value;
      
      try {
        const p = Number(principal);
        const pmt = Number(monthlyPayment);
        const paymentsPerYear = this.getPaymentsPerYear(frequencyValue);
        const totalPayments = (term / 12) * paymentsPerYear;

        // Use numerical method to find interest rate
        let rateGuess = 0.1; // Initial guess for periodic rate
        const tolerance = 0.0001;
        const maxIterations = 100;
        
        for (let i = 0; i < maxIterations; i++) {
          const pvFactor = Math.pow(1 + rateGuess, totalPayments);
          const pv = pmt * (pvFactor - 1) / (rateGuess * pvFactor);
          const diff = pv - p;
          
          if (Math.abs(diff) < tolerance) {
            break;
          }
          
          // Adjust guess
          rateGuess = rateGuess * (1 + diff / (p * 10));
        }
        
        const annualRate = rateGuess * paymentsPerYear * 100;
        this.paymentTermsForm.get('interestRate')?.setValue(annualRate.toFixed(2));
        
        // Calculate current balance
        this.calculateCurrentBalance();
      } catch (error) {
        NotificationUtils.getInstance().showError('Error calculating interest rate');
      }
    } else {
      NotificationUtils.getInstance().showError('Cannot calculate interest rate - missing required values');
    }
  }

  calculateTerm() {
    if (this.canCalculateTerm()) {
      const principal = LoanUtils.parseFormattedNumber(this.paymentTermsForm.get('principal')?.value);
      const monthlyPayment = LoanUtils.parseFormattedNumber(this.paymentTermsForm.get('monthlyPayment')?.value);
      const interestRate = LoanUtils.parseFormattedNumber(this.paymentTermsForm.get('interestRate')?.value);
      const frequencyValue = this.paymentTermsForm.get('frequency')?.value;
      
      try {
        const p = Number(principal);
        const pmt = Number(monthlyPayment);
        const r = Number(interestRate) / 100 / 12; // Monthly interest rate
        
        // Use the formula: n = -log(1 - P*r/PMT) / log(1+r)
        const termMonths = Math.log(1 / (1 - (p * r / pmt))) / Math.log(1 + r);
        
        // Convert term months to the selected frequency
        const term = this.convertMonthsToTerm(termMonths, frequencyValue);
        
        this.paymentTermsForm.get('term')?.setValue(term.toFixed(0));
        
        // Calculate current balance
        this.calculateCurrentBalance();
      } catch (error) {
        NotificationUtils.getInstance().showError('Error calculating term');
      }
    } else {
      NotificationUtils.getInstance().showError('Cannot calculate term - missing required values');
    }
  }

  /**
   * Calculates the current balance of the loan based on payment history
   */
  calculateCurrentBalance() {
    // Get the values from the form
    const principal = LoanUtils.parseFormattedNumber(this.paymentTermsForm.get('principal')?.value);
    const payment = LoanUtils.parseFormattedNumber(this.paymentTermsForm.get('monthlyPayment')?.value);
    const interestRate = Number(this.paymentTermsForm.get('interestRate')?.value);
    const term = Number(this.paymentTermsForm.get('term')?.value);
    const frequency = this.paymentTermsForm.get('frequency')?.value;
    
    if (principal && payment && interestRate && term && frequency) {
      // Calculate the current balance based on the amortization schedule
      this.calculateReviewData();
    }
  }

  /**
   * Get number of payments per year based on frequency
   */
  private getPaymentsPerYear(frequency: string): number {
    return LoanUtils.getPaymentsPerYear(frequency);
  }

  // Helper method to format currency input
  formatCurrency(event: any) {
    const value = event.target.value.replace(/[^\d.]/g, '');
    if (value) {
      event.target.value = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(value);
    }
  }

  /**
   * Creates an initial contact transaction for a new active loan or a loan that changed from pending to active
   * @param loan The loan to create a transaction for
   */
  private createInitialContactTransaction(loan: Loan) {
    if (!loan || !loan.id) {
      console.error('Cannot create contact transaction: Invalid loan or missing loan ID');
      return;
    }
    
    const transaction: Omit<BankTransaction, 'id'> = {
      ...InitBankTransaction,
      bank_id: loan.bank_id,
      contact_id: loan.contact_id,
      loan_id: loan.id,
      insurance_id: null,
      insurance_loan_id: null,
      category: 'Loan Funding',
      amount: -Number(loan.amount), 
      principal: 0, 
      interest: 0,
      late_fee: 0,
      transaction_date: formatDateAsYYYYMMDD(
        typeof loan.close_date === 'string' 
          ? parseYYYYMMDDToDate(loan.close_date) || new Date(loan.close_date)
          : loan.close_date as Date
      )
    };
    
    // Delete the id property since we're creating a new transaction
    delete (transaction as any).id;

    // Create the contact transaction
    this.bankStore.createChild({
      child: transaction,
      endpoint: 'bank_transaction',
      collectionName: 'bank_transactions_collection'
    })
    this.bankStore.selectLoan(null);
  }

  loadLoan() {
    const loan = this.bankStore.selectedLoan();
    if (loan) {
      this.isEdit = true;
      
      // Format dates for display
      const closeDateLuxon = loan.close_date ? DateTime.fromISO(String(loan.close_date)) : DateTime.now();
      const nextPaymentDateLuxon = loan.next_payment_date ? DateTime.fromISO(String(loan.next_payment_date)) : DateTime.now();
      
      // Set mid life date to the loan's mid_life_date if it exists, otherwise use next_payment_date
      const midLifeDateLuxon = loan.mid_life_date 
        ? DateTime.fromISO(String(loan.mid_life_date)) 
        : nextPaymentDateLuxon;
      
      // Patch the form with loan data
      this.basicDetailsForm.patchValue({
        loanType: loan.loan_type || 'existing', // Use the loan's loan_type or default to 'existing'
        contact: loan.contact_id,
        loanName: loan.name,
        state: loan.state, // Set the state from the existing loan
        lateFee: loan.late_fee,
        gracePeriod: loan.grace_period,
        closeDate: closeDateLuxon.toJSDate(),
        startPaymentDate: loan.start_payment_date ? DateTime.fromISO(String(loan.start_payment_date)).toJSDate() : null,
        nextPaymentDate: nextPaymentDateLuxon.toJSDate(),
        midLifeDate: midLifeDateLuxon.toJSDate(), // Use the loan's mid_life_date or next_payment_date
        fundFromBank: loan.state === 'active' // Only set to true for active loans
      });

      // Patch payment terms form
      this.paymentTermsForm.patchValue({
        principal: loan.amount,
        interestRate: loan.interest_rate,
        term: loan.term,
        frequency: loan.frequency || 'monthly',
        monthlyPayment: loan.payment,
      });

      // Calculate review data
      this.calculateReviewData();
    } else {
      this.setDefaultValues();
    }
  }

  setDefaultValues() {
    // Get today's date
    const today = new Date();
    
    // Set default values for basic details form
    this.basicDetailsForm.patchValue({
      closeDate: today,
      startPaymentDate: today,
      nextPaymentDate: today,
      midLifeDate: today // Initialize mid life date to today for new loans
    });
    
    // Default payment frequency to monthly
    this.paymentTermsForm.patchValue({
      frequency: 'monthly',
      term: null,
      interestRate: null,
      payment: null
    });
  }

  /**
   * Handles step change events from the stepper
   * @param event The step change event
   */
  onStepChange(event: any) {
    this.currentStepIndex = event.selectedIndex;
    
    // Calculate review data when reaching the review step
    if (this.currentStepIndex === 2) {
      this.calculateReviewData();
    }
  }

  /**
   * Marks all controls in a form group as touched to trigger validation
   */
  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }
}
function toObservable(loan_collections: (newName?: unknown) => unknown) {
  throw new Error('Function not implemented.');
}
